import { motion } from "framer-motion";
import { Star, Quote } from "lucide-react";
import PageShell from "@/components/site/PageShell";
import FinalCTA from "@/components/site/FinalCTA";
import ClientReels from "@/components/site/ClientReels";

const WRITTEN = [
  {
    name: "Marcus Holloway",
    company: "Apex Roofing Co.",
    role: "Founder & CEO",
    quote:
      "We tripled inbound leads in 4 months. EchoWings rebuilt our brand, site and SEO from scratch — the results compound every month.",
    avatar:
      "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?auto=format&fit=crop&q=80&w=400",
  },
  {
    name: "Priya Sharma",
    company: "Northbridge Clinic",
    role: "Director of Marketing",
    quote:
      "The most strategic agency we've ever worked with. We rank #1 for every search that matters and ChatGPT now cites us by name.",
    avatar:
      "https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&q=80&w=400",
  },
  {
    name: "David Okafor",
    company: "Driftboard SaaS",
    role: "Co-Founder",
    quote:
      "From generic startup look to a category-leader brand in 8 weeks. Our paid CAC dropped 38% just from the new site alone.",
    avatar:
      "https://images.pexels.com/photos/12311567/pexels-photo-12311567.jpeg?auto=compress&cs=tinysrgb&w=400",
  },
  {
    name: "Elena Russo",
    company: "Maison Hospitality",
    role: "Brand Director",
    quote:
      "The new site converts at 4.2% — up from 0.9%. Bookings are up 218% YoY and we finally look as premium as we feel.",
    avatar:
      "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&q=80&w=400",
  },
  {
    name: "Jordan Reyes",
    company: "Ironcore Build Co.",
    role: "Operations Lead",
    quote:
      "EchoWings runs our entire growth engine. Calls are up 240%, and we close 2× more deals because the brand finally signals quality.",
    avatar:
      "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&q=80&w=400",
  },
  {
    name: "Sofia Lin",
    company: "Lumen Apparel",
    role: "E-commerce Lead",
    quote:
      "Revenue up 186% in two quarters. The team treats our brand like their own — feels like an in-house team, not a vendor.",
    avatar:
      "https://images.unsplash.com/photo-1531123897727-8f129e1688ce?auto=format&fit=crop&q=80&w=400",
  },
];

const STATS = [
  { v: "4.9", l: "Avg. rating (150+ reviews)" },
  { v: "98%", l: "Client retention" },
  { v: "$25M+", l: "Tracked client revenue" },
  { v: "150+", l: "Happy clients" },
];

export default function ReviewsPage() {
  return (
    <PageShell testId="reviews-page">
      {/* Hero */}
      <section className="relative pt-16 pb-12 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[60rem] h-[60rem] radial-glow opacity-40 pointer-events-none" />
        <div className="relative max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-300 text-xs font-semibold tracking-widest uppercase">
              Reviews &amp; Reels
            </div>
            <h1 className="mt-6 font-display text-5xl md:text-6xl font-extrabold tracking-tight leading-[1.05]">
              Loved by{" "}
              <span className="text-gradient-brand">ambitious operators.</span>
            </h1>
            <p className="mt-6 text-lg text-white/65 max-w-2xl mx-auto">
              Real video reels and written reviews from the businesses we&apos;ve
              helped grow — direct, unfiltered, and measurable.
            </p>
            <div className="mt-6 inline-flex items-center gap-2">
              {[...Array(5)].map((_, k) => (
                <Star
                  key={k}
                  className="w-5 h-5 fill-cyan-400 text-cyan-400"
                />
              ))}
              <span className="ml-2 text-sm text-white/60">
                4.9 / 5 from 150+ clients
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="pb-8">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="rounded-3xl border border-white/10 bg-gradient-to-r from-blue-600/[0.08] via-cyan-400/[0.05] to-blue-600/[0.08] p-8 md:p-10">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {STATS.map((s) => (
                <div key={s.l}>
                  <div className="font-display text-3xl md:text-4xl font-extrabold text-gradient-brand">
                    {s.v}
                  </div>
                  <div className="mt-1 text-xs text-white/55">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Reels */}
      <ClientReels showViewAll={false} />

      {/* Written reviews grid */}
      <section className="section pt-0" data-testid="reviews-written">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-300 text-xs font-semibold tracking-widest uppercase">
              Written Reviews
            </div>
            <h2 className="mt-4 font-display text-3xl md:text-4xl font-bold tracking-tight">
              What partners are saying.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {WRITTEN.map((t, i) => (
              <motion.article
                key={t.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                data-testid={`review-card-${i}`}
                className="group relative rounded-3xl border border-white/10 bg-[#0a0a0a] p-7 hover:border-cyan-500/30 transition-colors flex flex-col"
              >
                <Quote className="absolute top-6 right-6 w-9 h-9 text-cyan-500/10" />
                <div className="flex items-center gap-1">
                  {[...Array(5)].map((_, k) => (
                    <Star
                      key={k}
                      className="w-3.5 h-3.5 fill-cyan-400 text-cyan-400"
                    />
                  ))}
                </div>
                <p className="mt-5 text-white/85 leading-relaxed flex-1">
                  &ldquo;{t.quote}&rdquo;
                </p>
                <div className="mt-7 pt-6 border-t border-white/5 flex items-center gap-3">
                  <img
                    src={t.avatar}
                    alt={t.name}
                    className="w-11 h-11 rounded-full object-cover border border-white/10"
                    loading="lazy"
                  />
                  <div>
                    <div className="font-semibold text-white text-sm">
                      {t.name}
                    </div>
                    <div className="text-xs text-white/50">
                      {t.role} · {t.company}
                    </div>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      <FinalCTA />
    </PageShell>
  );
}
